import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Check, ClipboardCheck, Download, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSelectedCountry } from "@/context/SelectedCountryContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRIP_TYPES = ["leisure", "business", "family", "student"];

const storageKey = (country, tripType, duration) =>
  `csa_checklist_${country}_${tripType}_${duration}`;

export default function Checklist({ embed = false }) {
  const { t } = useLanguage();
  const { slug: country, country: countryInfo } = useSelectedCountry();
  const [tripType, setTripType] = useState("leisure");
  const [duration, setDuration] = useState(7);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const printRef = useRef(null);

  useEffect(() => {
    // Country selection is managed globally; nothing to fetch here.
  }, []);

  // Restore persisted state for the active key
  const restoreFor = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { checked: {}, customItems: [] };
      const parsed = JSON.parse(raw);
      return { checked: parsed.checked || {}, customItems: parsed.customItems || [] };
    } catch { return { checked: {}, customItems: [] }; }
  };

  const persist = (key, next) => {
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* quota */ }
  };

  const generate = async () => {
    if (!country) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/checklist/generate`, {
        country_slug: country,
        trip_type: tripType,
        duration_days: parseInt(duration, 10) || 7,
      });
      setResult(r.data);
      const restored = restoreFor(storageKey(country, tripType, duration));
      setChecked(restored.checked);
      setCustomItems(restored.customItems);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally { setLoading(false); }
  };

  // Persist whenever checked or customItems change
  useEffect(() => {
    if (!result) return;
    const key = storageKey(country, tripType, duration);
    persist(key, { checked, customItems });
  }, [checked, customItems, result, country, tripType, duration]);

  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));

  const addCustom = () => {
    const text = newItem.trim();
    if (!text) return;
    setCustomItems((prev) => [...prev, { id: `u-${Date.now()}`, text }]);
    setNewItem("");
  };

  const removeCustom = (id) => {
    setCustomItems((prev) => prev.filter((it) => it.id !== id));
    setChecked((c) => { const n = { ...c }; delete n[id]; return n; });
  };

  const resetProgress = () => {
    if (!window.confirm("Clear all checks and custom items for this checklist?")) return;
    setChecked({}); setCustomItems([]);
    try { localStorage.removeItem(storageKey(country, tripType, duration)); } catch { /* ignore */ }
  };

  const allItems = useMemo(() => {
    if (!result) return [];
    const universal = result.items.map((text, i) => ({ id: `c-${i}`, text, group: "universal" }));
    const briefs = result.country_specific.map((text, i) => ({ id: `b-${i}`, text, group: "brief" }));
    const custom = customItems.map((it) => ({ id: it.id, text: it.text, group: "custom" }));
    return [...universal, ...briefs, ...custom];
  }, [result, customItems]);

  const doneCount = allItems.filter((it) => checked[it.id]).length;
  const totalCount = allItems.length;
  const pct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  const printChecklist = () => {
    if (!printRef.current) return;
    const w = window.open("", "PRINT", "height=900,width=900");
    w.document.write(`<html><head><title>Trip Checklist — ${result.country}</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,sans-serif;color:#0F172A;padding:40px;max-width:780px;margin:auto;}
        h1{font-size:28px;margin:0 0 4px;}
        .meta{color:#475569;font-size:13px;margin-bottom:24px;}
        h2{font-size:14px;text-transform:uppercase;letter-spacing:2px;color:#475569;margin-top:24px;margin-bottom:8px;border-top:1px solid #E2E8F0;padding-top:14px;}
        ul{list-style:none;padding:0;margin:0;}
        li{padding:6px 0;border-bottom:1px dashed #E2E8F0;display:flex;gap:10px;align-items:flex-start;}
        .box{width:14px;height:14px;border:1.5px solid #94A3B8;display:inline-block;margin-top:3px;flex-shrink:0;}
        .done{text-decoration:line-through;color:#94A3B8;}
        .footer{margin-top:30px;font-size:11px;color:#94A3B8;}
      </style></head><body>
      <h1>Trip Checklist — ${result.country}</h1>
      <div class="meta">${t.checklist.types[result.trip_type]} · ${result.duration_days} days · ${doneCount}/${totalCount} complete</div>
      <h2>${t.checklist.common_items}</h2>
      <ul>${result.items.map((x, i) => `<li><span class="box"></span><span class="${checked[`c-${i}`] ? "done" : ""}">${x}</span></li>`).join("")}</ul>
      <h2>${t.checklist.country_items}</h2>
      <ul>${result.country_specific.map((x, i) => `<li><span class="box"></span><span class="${checked[`b-${i}`] ? "done" : ""}">${x}</span></li>`).join("")}</ul>
      ${customItems.length ? `<h2>Your Custom Items</h2><ul>${customItems.map((it) => `<li><span class="box"></span><span class="${checked[it.id] ? "done" : ""}">${it.text}</span></li>`).join("")}</ul>` : ""}
      <div class="footer">Generated by Civic Sense Abroad — civicsenseabroad.com</div>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 350);
  };

  return (
    <div data-testid="checklist-page" className={embed ? "" : "fade-in max-w-6xl mx-auto px-6 lg:px-12 py-16"}>
      {!embed && (
        <div className="mb-12 max-w-2xl">
          <div className="overline mb-3">Personalized Prep</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]">
            {t.checklist.title}
          </h1>
          <p className="mt-4 text-[#475569] text-lg leading-relaxed">{t.checklist.sub}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Form */}
        <div className="lg:col-span-4">
          <div className="card p-6 lg:p-8 lg:sticky lg:top-24">
            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  {t.checklist.destination}
                </label>
                <div
                  data-testid="checklist-country-chip"
                  className="input-field flex items-center gap-2 bg-[#EEF2FF] border-[#002FA7]/30 text-[#0F172A]"
                >
                  {countryInfo ? (
                    <>
                      <span className="text-lg">{countryInfo.flag}</span>
                      <span className="font-medium">{countryInfo.name}</span>
                      <span className="ml-auto text-xs text-[#475569]">change via header</span>
                    </>
                  ) : (
                    <span className="text-[#475569] text-sm">Pick a destination from the header.</span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  {t.checklist.trip_type}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {TRIP_TYPES.map((tt) => (
                    <button
                      key={tt}
                      data-testid={`trip-type-${tt}`}
                      onClick={() => setTripType(tt)}
                      className={`p-2 text-sm rounded-sm border transition-all ${
                        tripType === tt
                          ? "border-[#002FA7] bg-[#EEF2FF] text-[#002FA7] font-medium"
                          : "border-[#E2E8F0] text-[#475569] hover:border-[#94A3B8]"
                      }`}
                    >
                      {t.checklist.types[tt]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  {t.checklist.duration}
                </label>
                <input
                  data-testid="checklist-duration"
                  type="number"
                  min={1}
                  max={365}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="input-field"
                />
              </div>

              <button
                data-testid="checklist-generate-button"
                onClick={generate}
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading ? "Generating…" : t.checklist.generate}
                {!loading && <ClipboardCheck className="w-4 h-4" strokeWidth={1.5} />}
              </button>

              {result && (
                <>
                  <div className="pt-2">
                    <div className="flex items-center justify-between mb-2 text-xs text-[#475569]">
                      <span data-testid="checklist-progress-text" className="font-medium">{doneCount} of {totalCount} done</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
                      <div data-testid="checklist-progress-bar" className="h-full bg-[#002FA7] transition-all duration-300" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <button data-testid="checklist-reset" onClick={resetProgress} className="btn-secondary w-full justify-center text-sm">
                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} /> Reset progress
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-8 space-y-8">
          {!result && (
            <div className="card p-12 text-center text-[#475569]">
              <ClipboardCheck className="w-10 h-10 mx-auto mb-4 text-[#94A3B8]" strokeWidth={1.5} />
              <p>Select a destination and click generate to see your custom checklist.</p>
            </div>
          )}

          {result && (
            <div ref={printRef}>
              <div className="card p-8 mb-8">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <div className="overline mb-1">Your Trip</div>
                    <h2 className="font-display text-xl font-semibold tracking-tight">
                      {result.country} · {t.checklist.types[result.trip_type]} · {result.duration_days} days
                    </h2>
                  </div>
                  <button onClick={printChecklist} data-testid="checklist-print" className="btn-secondary text-sm">
                    <Download className="w-4 h-4" strokeWidth={1.5} /> Print / Save
                  </button>
                </div>
              </div>

              <div className="card p-8 mb-8">
                <div className="overline mb-4">{t.checklist.common_items}</div>
                <ul className="space-y-3">
                  {result.items.map((item, i) => {
                    const id = `c-${i}`;
                    const isOn = !!checked[id];
                    return (
                      <li key={id} data-testid={`checklist-item-${i}`} className="flex items-start gap-3">
                        <button
                          data-testid={`checklist-toggle-${id}`}
                          onClick={() => toggle(id)}
                          className={`shrink-0 w-5 h-5 mt-0.5 rounded-sm border flex items-center justify-center transition-all ${
                            isOn ? "bg-[#002FA7] border-[#002FA7]" : "border-[#94A3B8]"
                          }`}
                        >
                          {isOn && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </button>
                        <span className={`text-sm leading-relaxed ${isOn ? "line-through text-[#94A3B8]" : "text-[#0F172A]"}`}>
                          {item}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="card p-8 mb-8 bg-white border-l-4 border-l-[#002FA7]">
                <div className="overline mb-4 text-[#002FA7]">{t.checklist.country_items}</div>
                <ul className="space-y-3">
                  {result.country_specific.map((item, i) => {
                    const id = `b-${i}`;
                    const isOn = !!checked[id];
                    const m = item.match(/^\[([^\]]+)\]\s*(.*)$/);
                    const tag = m ? m[1] : null;
                    const text = m ? m[2] : item;
                    return (
                      <li key={id} data-testid={`country-brief-${i}`} className="flex items-start gap-3">
                        <button
                          data-testid={`checklist-toggle-${id}`}
                          onClick={() => toggle(id)}
                          className={`shrink-0 w-5 h-5 mt-0.5 rounded-sm border flex items-center justify-center transition-all ${
                            isOn ? "bg-[#002FA7] border-[#002FA7]" : "border-[#94A3B8]"
                          }`}
                        >
                          {isOn && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </button>
                        <div className={`text-sm leading-relaxed ${isOn ? "line-through text-[#94A3B8]" : ""}`}>
                          {tag && (
                            <span className="inline-block mr-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#002FA7] bg-[#EEF2FF] px-1.5 py-0.5 rounded-sm align-middle">
                              {tag}
                            </span>
                          )}
                          <span className={isOn ? "text-[#94A3B8]" : "text-[#0F172A]"}>{text}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Custom items section */}
              <div className="card p-8">
                <div className="overline mb-4">Your Custom Items</div>
                <ul className="space-y-3 mb-5">
                  {customItems.length === 0 && (
                    <li className="text-sm text-[#94A3B8] italic">Add your own items below — passport copies, gifts to pack, meetings, anything.</li>
                  )}
                  {customItems.map((it) => {
                    const isOn = !!checked[it.id];
                    return (
                      <li key={it.id} data-testid={`custom-item-${it.id}`} className="flex items-start gap-3 group">
                        <button
                          onClick={() => toggle(it.id)}
                          className={`shrink-0 w-5 h-5 mt-0.5 rounded-sm border flex items-center justify-center transition-all ${
                            isOn ? "bg-[#002FA7] border-[#002FA7]" : "border-[#94A3B8]"
                          }`}
                        >
                          {isOn && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </button>
                        <span className={`flex-1 text-sm leading-relaxed ${isOn ? "line-through text-[#94A3B8]" : "text-[#0F172A]"}`}>
                          {it.text}
                        </span>
                        <button
                          data-testid={`remove-custom-${it.id}`}
                          onClick={() => removeCustom(it.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-[#94A3B8] hover:text-[#991B1B]"
                          aria-label="Remove"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex gap-2">
                  <input
                    data-testid="custom-item-input"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                    placeholder="Add your own item…"
                    className="input-field flex-1"
                  />
                  <button data-testid="add-custom-item" onClick={addCustom} className="btn-primary shrink-0">
                    <Plus className="w-4 h-4" strokeWidth={1.5} /> Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
